import React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const MAX_DEPTH = 6;

const ErrorTooltip = ({ errors, children }) => {
	if (errors.length === 0) return children;
	return (
		<Tooltip>
			<TooltipTrigger asChild>{children}</TooltipTrigger>
			<TooltipContent side="bottom" className="max-w-md w-max z-50">
				{errors.map((err, i) => (
					<div key={i} className="flex items-start gap-1.5 text-xs text-red-400 py-0.5">
						<span className="w-1 h-1 rounded-full bg-red-500 shrink-0 mt-1.5" />
						<span className="break-words">{err.message}</span>
					</div>
				))}
			</TooltipContent>
		</Tooltip>
	);
};

const BodyFieldTree = ({ fields = [], depth = 0 }) => {
	if (fields.length === 0 && depth === 0) {
		return <div className="text-xs text-muted-foreground italic px-2 py-1">No body fields</div>;
	}

	return (
		<div>
			{fields.map((field, i) => (
				<div key={i}>
					<div
						className="flex items-center gap-1 py-0.5 font-mono text-xs"
						style={{ paddingLeft: `${8 + depth * 16}px` }}
					>
						<span className="text-foreground">{field.name}</span>
						<span className="text-muted-foreground">: {field.type}</span>
						{field.required && <span className="text-primary">*</span>}
					</div>
					{field.children?.length > 0 && depth < MAX_DEPTH && (
						<BodyFieldTree fields={field.children} depth={depth + 1} />
					)}
				</div>
			))}
		</div>
	);
};

const BodyFieldComparison = ({ consumerFields = [], producerFields = [], errors = [], section }) => {
	const hasConsumer = consumerFields.length > 0;
	const hasProducer = producerFields.length > 0;

	if (!hasConsumer && !hasProducer) {
		return <div className="text-xs text-muted-foreground italic px-2 py-1">No body fields</div>;
	}

	// Path-indexed errors (preferred when fieldPath is available)
	const errorsByPath = new Map();
	errors
		.filter((e) => e.section === section && e.fieldPath)
		.forEach((e) => {
			const existing = errorsByPath.get(e.fieldPath) || [];
			existing.push(e);
			errorsByPath.set(e.fieldPath, existing);
		});

	// Fallback: flat field name matching for errors without fieldPath
	const errorFieldNames = new Set(
		errors.filter((e) => e.section === section && e.fieldName && !e.fieldPath).map((e) => e.fieldName.toLowerCase())
	);

	const setLevelErrors = errors.filter((e) => e.section === section && !e.fieldName);

	const HighlightedFieldTree = ({ fields = [], depth = 0, parentPath = '' }) => {
		if (fields.length === 0 && depth === 0) {
			return <div className="text-xs text-muted-foreground italic px-2 py-1">No body fields</div>;
		}

		return (
			<div>
				{fields.map((field, i) => {
					const currentPath = `${parentPath}/${field.name}`;
					const pathErrors = errorsByPath.get(currentPath) || [];
					const nameFallback = errorFieldNames.has(field.name?.toLowerCase());
					const fieldErrors =
						pathErrors.length > 0
							? pathErrors
							: nameFallback
								? errors.filter(
										(e) =>
											e.section === section &&
											e.fieldName?.toLowerCase() === field.name?.toLowerCase()
									)
								: [];
					const hasError = fieldErrors.length > 0;

					return (
						<div key={i}>
							<div
								className="flex items-center gap-1 py-0.5 font-mono text-xs"
								style={{ paddingLeft: `${8 + depth * 16}px` }}
							>
								{hasError ? (
									<ErrorTooltip errors={fieldErrors}>
										<span className="text-red-400 decoration-red-400/40 decoration-dashed underline underline-offset-2 cursor-help">
											{field.name}
										</span>
									</ErrorTooltip>
								) : (
									<span className="text-foreground">{field.name}</span>
								)}
								<span className={hasError ? 'text-red-400/70' : 'text-muted-foreground'}>
									: {field.type}
								</span>
								{field.required && (
									<span className={hasError ? 'text-red-400' : 'text-primary'}>*</span>
								)}
							</div>
							{field.children?.length > 0 && depth < MAX_DEPTH && (
								<HighlightedFieldTree
									fields={field.children}
									depth={depth + 1}
									parentPath={currentPath}
								/>
							)}
						</div>
					);
				})}
			</div>
		);
	};

	return (
		<div>
			<div className="grid grid-cols-2 gap-px">
				<HighlightedFieldTree fields={consumerFields} />
				<HighlightedFieldTree fields={producerFields} />
			</div>

			{setLevelErrors.length > 0 && (
				<div className="px-2 py-1 border-t border-border/10">
					{setLevelErrors.map((err, i) => (
						<div key={i} className="flex items-center gap-1 text-[10px] text-red-400/70">
							<span className="w-1 h-1 rounded-full bg-red-500/50 shrink-0" />
							{err.message}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export { BodyFieldTree, BodyFieldComparison };
export default BodyFieldComparison;
